import {
 Avatar,
 Autocomplete,
 Box,
 Button,
 Chip,
 Dialog,
 DialogActions,
 DialogContent,
 DialogTitle,
 FormControl,
 FormControlLabel,
 InputLabel,
 MenuItem,
 Select,
 Switch,
 TextField,
 Typography,
 useMediaQuery,
 useTheme,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import themeOptions from '../../utils/theme';
import CloseButton from './CloseButton';
import XTabs from './XTabs';

interface UserProfileFormData {
 language: string;
 email: string;
 phone: string;
 representing: string;
 group: string[];
 notificationMethod: string[];
 twoFactorEnabled: boolean;
 currentPassword: string;
 newPassword: string;
 repeatPassword: string;
}

interface UserProfileModalProps {
 open: boolean;
 onClose: () => void;
}

const UserProfileModal: React.FC<UserProfileModalProps> = ({
 open,
 onClose,
}) => {
 const { t, i18n } = useTranslation();
 const theme = useTheme();
 const isLargerThanMd = useMediaQuery(themeOptions().breakpoints.up('md'));
 const [twoFactorEnabled, setTwoFactorEnabled] = useState(false);
 const [activeTab, setActiveTab] = useState(0);

 const defaultValues: UserProfileFormData = {
  language: i18n.language || 'lt',
  email: 'vardas.pavarde@gmail.com',
  phone: '+370 783 93 123',
  representing: 'demo@kemek.eu - Openlane',
  group: ['Grupė'],
  notificationMethod: ['SMS', 'Email'],
  twoFactorEnabled: false,
  currentPassword: '',
  newPassword: '',
  repeatPassword: '',
 };

 const {
  control,
  handleSubmit,
  reset,
  watch,
  setValue,
  formState: { errors },
 } = useForm<UserProfileFormData>({ defaultValues });

 const watchedTwoFactor = watch('twoFactorEnabled');

 useEffect(() => {
  if (open) {
   reset(defaultValues);
   setTwoFactorEnabled(false);
   setActiveTab(0);
  }
 }, [open, reset]);

 useEffect(() => {
  setTwoFactorEnabled(watchedTwoFactor);
 }, [watchedTwoFactor]);

 const handleFormSubmit = (data: UserProfileFormData) => {
  console.log('Form submitted:', data);
  if (activeTab === 1) {
   console.log('Password change:', {
    currentPassword: data.currentPassword,
    newPassword: data.newPassword,
   });
  }
  onClose();
 };

 const handleSaveClick = () => {
  if (activeTab === 0) {
   handleSubmit(handleFormSubmit)();
  } else {
   const form = document.getElementById(
    'password-change-form',
   ) as HTMLFormElement;
   if (form) {
    form.requestSubmit();
   }
  }
 };

 const handleCancel = () => {
  reset(defaultValues);
  setTwoFactorEnabled(false);
  onClose();
 };

 const handleGenerateQR = () => {
  console.log('Generate QR code');
 };

 const user = {
  name: 'Vardas Pavardė',
  role: 'Operatorius',
  avatar: 'VP',
 };

 const languageOptions = [
  { value: 'lt', label: 'Lietuvių' },
  { value: 'en', label: 'English' },
 ];

 const representingOptions = [
  { value: 'demo@kemek.eu - Openlane', label: 'demo@kemek.eu - Openlane' },
 ];

 const groupOptions = ['Grupė', 'Grupė 2', 'Grupė 3'];

 const notificationOptions = ['SMS', 'Email', 'Push'];

 const userTabContent = (
  <form id="user-profile-form" onSubmit={handleSubmit(handleFormSubmit)}>
   <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
    <Box
     sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 2,
     }}
    >
     <Avatar
      sx={{
       width: 64,
       height: 64,
       bgcolor: (th) => th.palette.kemek.lightGray,
       color: (th) => th.palette.kemek.darkestGray,
       fontSize: '20px',
       fontWeight: 500,
      }}
     >
      {user.avatar}
     </Avatar>
     <Box>
      <Typography
       sx={{
        fontSize: '18px',
        fontWeight: 500,
        color: (th) => th.palette.kemek.darkestGray,
       }}
      >
       {user.name}
      </Typography>
      <Typography
       sx={{
        fontSize: '14px',
        color: (th) => th.palette.kemek.lightGray,
       }}
      >
       {user.role}
      </Typography>
     </Box>
    </Box>

    <Box>
     <Typography
      sx={{
       fontSize: '14px',
       color: (th) => th.palette.kemek.darkGray,
       fontWeight: 500,
       mb: 2,
      }}
     >
      Vartotojo nustatymai
     </Typography>

     <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <FormControl fullWidth error={!!errors.language}>
       <InputLabel id="language-label">Kalba</InputLabel>
       <Controller
        name="language"
        control={control}
        rules={{ required: 'Language is required' }}
        render={({ field }) => (
         <Select
          {...field}
          labelId="language-label"
          id="language"
          label="Kalba"
          sx={{ backgroundColor: (th) => th.palette.background.paper }}
         >
          {languageOptions.map((option) => (
           <MenuItem key={option.value} value={option.value}>
            {option.label}
           </MenuItem>
          ))}
         </Select>
        )}
       />
      </FormControl>

      <Box sx={{ display: 'flex', gap: 1 }}>
       <FormControl fullWidth error={!!errors.email}>
        <Controller
         name="email"
         control={control}
         rules={{
          required: 'Email is required',
          pattern: {
           value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
           message: 'Invalid email address',
          },
         }}
         render={({ field }) => (
          <TextField
           {...field}
           id="email"
           type="email"
           label="El. paštas"
           variant="outlined"
           error={!!errors.email}
           helperText={errors.email?.message}
           sx={{ backgroundColor: (th) => th.palette.background.paper }}
          />
         )}
        />
       </FormControl>
       <Button variant="outlined" size="medium" sx={{ minHeight: 44 }}>
        Patvirtinti
       </Button>
      </Box>

      <Box sx={{ display: 'flex', gap: 1 }}>
       <FormControl fullWidth error={!!errors.phone}>
        <Controller
         name="phone"
         control={control}
         rules={{ required: 'Phone number is required' }}
         render={({ field }) => (
          <TextField
           {...field}
           id="phone"
           type="tel"
           label="Tel. numeris"
           variant="outlined"
           error={!!errors.phone}
           helperText={errors.phone?.message}
           sx={{ backgroundColor: (th) => th.palette.background.paper }}
          />
         )}
        />
       </FormControl>
       <Button variant="outlined" size="medium" sx={{ minHeight: 44 }}>
        Patvirtinti
       </Button>
      </Box>

      <FormControl fullWidth error={!!errors.representing}>
       <InputLabel id="representing-label">Atstovaujate</InputLabel>
       <Controller
        name="representing"
        control={control}
        rules={{ required: 'Representing is required' }}
        render={({ field }) => (
         <Select
          {...field}
          labelId="representing-label"
          id="representing"
          label="Atstovaujate"
          sx={{ backgroundColor: (th) => th.palette.background.paper }}
         >
          {representingOptions.map((option) => (
           <MenuItem key={option.value} value={option.value}>
            {option.label}
           </MenuItem>
          ))}
         </Select>
        )}
       />
      </FormControl>

      <FormControl fullWidth error={!!errors.group}>
       <Controller
        name="group"
        control={control}
        render={({ field }) => (
         <Autocomplete
          {...field}
          multiple
          options={groupOptions}
          value={field.value || []}
          onChange={(_, value) => field.onChange(value)}
          renderTags={(value, getTagProps) =>
           value.map((option, index) => (
            <Chip
             {...getTagProps({ index })}
             key={option}
             label={option}
             onDelete={() => {
              const newValue = field.value.filter((_, i) => i !== index);
              field.onChange(newValue);
             }}
            />
           ))
          }
          renderInput={(params) => (
           <TextField
            {...params}
            label="Grupė"
            variant="outlined"
            sx={{ backgroundColor: (th) => th.palette.background.paper }}
           />
          )}
         />
        )}
       />
      </FormControl>

      <FormControl fullWidth error={!!errors.notificationMethod}>
       <Controller
        name="notificationMethod"
        control={control}
        render={({ field }) => (
         <Autocomplete
          {...field}
          multiple
          options={notificationOptions}
          value={field.value || []}
          onChange={(_, value) => field.onChange(value)}
          renderTags={(value, getTagProps) =>
           value.map((option, index) => (
            <Chip
             {...getTagProps({ index })}
             key={option}
             label={option}
             onDelete={() => {
              const newValue = field.value.filter((_, i) => i !== index);
              field.onChange(newValue);
             }}
            />
           ))
          }
          renderInput={(params) => (
           <TextField
            {...params}
            label="Pranešimu gavimo budas"
            variant="outlined"
            sx={{ backgroundColor: (th) => th.palette.background.paper }}
           />
          )}
         />
        )}
       />
      </FormControl>
     </Box>
    </Box>

    <Box
     sx={{
      display: 'flex',
      flexDirection: 'column',
      gap: 2,
      pt: 2,
      borderTop: 1,
      borderTopColor: (th) => th.palette.kemek.lightestGray,
     }}
    >
     <Box
      sx={{
       display: 'flex',
       flexDirection: 'column',
       gap: 2,
       //  alignItems: 'center',
       //  justifyContent: 'space-between',
      }}
     >
      <Controller
       name="twoFactorEnabled"
       control={control}
       render={({ field }) => (
        <FormControlLabel
         control={
          <Switch
           checked={field.value}
           onChange={(e) => {
            field.onChange(e.target.checked);
            setTwoFactorEnabled(e.target.checked);
           }}
           color="primary"
          />
         }
         label="Dviejų faktorių autentifikacija (2FA)"
         sx={{ m: 0 }}
        />
       )}
      />
      <Button
       variant="outlined"
       onClick={handleGenerateQR}
       disabled={!twoFactorEnabled}
       size="medium"
       sx={{ display: 'flex', width: '100%', minHeight: 44 }}
      >
       Generuoti QR kodą.
      </Button>
     </Box>
    </Box>
   </Box>
  </form>
 );

 const passwordTabContent = (
  <form id="password-change-form" onSubmit={handleSubmit(handleFormSubmit)}>
   <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
    <Typography
     sx={{
      fontSize: '14px',
      color: (th) => th.palette.kemek.darkGray,
      fontWeight: 500,
     }}
    >
     Slaptažodžio keitimas
    </Typography>

    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
     <FormControl fullWidth error={!!errors.currentPassword}>
      <Controller
       name="currentPassword"
       control={control}
       rules={{ required: 'Current password is required' }}
       render={({ field }) => (
        <TextField
         {...field}
         id="currentPassword"
         type="password"
         label="Dabartinis slaptažodis"
         variant="outlined"
         error={!!errors.currentPassword}
         helperText={errors.currentPassword?.message}
         sx={{ backgroundColor: (th) => th.palette.background.paper }}
        />
       )}
      />
     </FormControl>

     <FormControl fullWidth error={!!errors.newPassword}>
      <Controller
       name="newPassword"
       control={control}
       rules={{
        required: 'New password is required',
        minLength: {
         value: 8,
         message: 'Password must be at least 8 characters long',
        },
       }}
       render={({ field }) => (
        <TextField
         {...field}
         id="newPassword"
         type="password"
         label="Naujas slaptažodis"
         variant="outlined"
         error={!!errors.newPassword}
         helperText={errors.newPassword?.message}
         sx={{ backgroundColor: (th) => th.palette.background.paper }}
        />
       )}
      />
     </FormControl>

     <FormControl fullWidth error={!!errors.repeatPassword}>
      <Controller
       name="repeatPassword"
       control={control}
       rules={{
        required: 'Please repeat the new password',
        validate: (value) =>
         value === watch('newPassword') || 'Passwords do not match',
       }}
       render={({ field }) => (
        <TextField
         {...field}
         id="repeatPassword"
         type="password"
         label="Pakartokite naują slaptažodi"
         variant="outlined"
         error={!!errors.repeatPassword}
         helperText={errors.repeatPassword?.message}
         sx={{ backgroundColor: (th) => th.palette.background.paper }}
        />
       )}
      />
     </FormControl>
    </Box>
   </Box>
  </form>
 );

 const tabs = [
  { id: 'user', label: 'Vartotojas', content: userTabContent },
  { id: 'password', label: 'Slaptažodis', content: passwordTabContent },
 ];

 return (
  <Dialog
   open={open}
   onClose={onClose}
   fullWidth
   fullScreen={!isLargerThanMd}
   sx={{
    '& .MuiDialog-container': {
     '& .MuiPaper-root': { width: '100%', maxWidth: '400px' },
    },
   }}
  >
   <DialogTitle>Vartotojo paskyra</DialogTitle>

   <CloseButton closeDialog={onClose} />

   <DialogContent sx={{ py: 0, px: 0 }}>
    <XTabs
     tabs={tabs}
     defaultTab={0}
     variant="fullWidth"
     ariaLabel="user profile tabs"
     sx={{ px: 3 }}
     tabSx={{
      '& .Mui-selected': {
       color: (th) => th.palette.kemek.blue,
      },
      '& .MuiTabs-indicator': {
       backgroundColor: (th) => th.palette.kemek.blue,
      },
     }}
     panelSx={{ p: 0 }}
     onChange={(newValue) => setActiveTab(newValue)}
    />
   </DialogContent>

   <DialogActions sx={{ padding: '16px 24px', justifyContent: 'flex-end' }}>
    <Box sx={{ display: 'flex', gap: 1 }}>
     <Button type="button" onClick={handleCancel} size="large">
      {t('global.cancel')}
     </Button>
     <Button
      variant="contained"
      type="submit"
      form={activeTab === 0 ? 'user-profile-form' : 'password-change-form'}
      size="large"
      onClick={handleSaveClick}
     >
      Išsaugoti
     </Button>
    </Box>
   </DialogActions>
  </Dialog>
 );
};

export default UserProfileModal;
